import {
  assert,
  reportError,
  createMediaSrc,
  parseMediaSrc,
  getMediaDataFromSrc,
  getVideoDataByVideoId,
  isValidMediaSrc,
} from '@wix/editor-elements-corvid-utils';
import type { BackgroundFittingType, BackgroundProps } from './types';

export default function createSdk(
  getProps: () => BackgroundProps,
  setProps: (props: Partial<BackgroundProps>) => void,
): any {
  return {
    get src() {
      const props = getProps();

      // If background has video, return video src
      if (props.videoMedia?.uri) {
        return (
          createMediaSrc({
            type: 'video',
            mediaId: props.videoMedia.uri,
            title: props.videoMedia.name,
            width: props.videoMedia.sources?.[0]?.width,
            height: props.videoMedia.sources?.[0]?.height,
            posterId: props.videoMedia.poster?.uri,
            duration: props.videoMedia.duration,
          }).item ?? ''
        );
      }

      // If background has image, return image src
      if (props.image) {
        return (
          createMediaSrc({
            type: 'image',
            mediaId: props.image.uri,
            title: props.image.name,
            width: props.image.width,
            height: props.image.height,
          }).item ?? ''
        );
      }

      return '';
    },

    set src(value) {
      const src = assert.isNil(value) ? '' : value;
      if (src === '') {
        setProps({
          videoMedia: undefined,
          loop: undefined,
          muted: undefined,
          autoplay: undefined,
          playbackRate: undefined,
          qualityPolicy: undefined,
          posterEffect: undefined,
          image: undefined,
        });
        return;
      }
      if (isValidMediaSrc(src, 'video')) {
        const mediaData = getMediaDataFromSrc(src);
        if (!mediaData || mediaData.type !== 'WixVideo' || !mediaData.videoId) {
          return;
        }

        const preservedProps = getProps();

        getVideoDataByVideoId(mediaData.videoId)
          .then(fullVideoRefData => {
            const updatedVideoMedia = {
              uri: fullVideoRefData.mediaObject.videoId || '',
              name: mediaData.name || fullVideoRefData.mediaObject.title || '',
              sources:
                fullVideoRefData.mediaObject.qualities?.map((quality: any) => ({
                  quality: quality.quality,
                  width: quality.width,
                  height: quality.height,
                  types: [
                    {
                      format: quality.format || 'mp4',
                      uri: quality.url,
                    },
                  ],
                })) || [],
              adaptiveSources:
                fullVideoRefData.mediaObject.adaptiveVideo?.map(
                  (adaptive: any) => ({
                    format: adaptive.format,
                    uri: adaptive.url,
                  }),
                ) || [],
              hasAudio: fullVideoRefData.mediaObject.hasAudio || false,
              fps: fullVideoRefData.mediaObject.fps
                ? Number(fullVideoRefData.mediaObject.fps)
                : undefined,
              duration: fullVideoRefData.mediaObject.duration,
              poster: fullVideoRefData.mediaObject.posterImageRef,
            };

            setProps({
              image: undefined,
              videoMedia: updatedVideoMedia,
              loop: preservedProps.loop ?? true,
              muted: preservedProps.muted ?? true,
              autoplay: preservedProps.autoplay ?? true,
              playbackRate: preservedProps.playbackRate ?? '1.0',
              qualityPolicy: preservedProps.qualityPolicy ?? 'proportional',
            });
          })
          .catch(error => {
            reportError(error);
          });
        return;
      }

      // Try to parse as image
      const {
        height,
        width,
        title,
        error,
        mediaId: uri,
      } = parseMediaSrc(src.toString(), 'image');

      if (error) {
        reportError(
          `The "src" property cannot be set to "${src}". It must be a valid URL starting with "http://", "https://", "wix:image://", or "wix:video://".`,
        );
        return;
      }

      const currentData = getProps();
      const updatedImageData = {
        width: width || 0,
        height: height || 0,
        uri: uri || '',
        name: title || '',
        alt: title || '',
      };

      setProps({
        videoMedia: undefined,
        loop: undefined,
        muted: undefined,
        autoplay: undefined,
        playbackRate: undefined,
        qualityPolicy: undefined,
        posterEffect: undefined,
        image: updatedImageData,
        fittingType: currentData?.fittingType || 'fill',
      });
    },

    get title() {
      const props = getProps();
      if (props.videoMedia?.name) {
        return props.videoMedia.name;
      }
      if (props.image?.name) {
        return props.image.name;
      }
      return '';
    },

    set title(value) {
      const title = assert.isNil(value) ? '' : value.toString();
      const props = getProps();

      if (props.videoMedia?.uri) {
        setProps({
          videoMedia: {
            ...props.videoMedia,
            name: title,
          },
        });
      } else if (props.image?.uri) {
        setProps({
          image: {
            ...props.image,
            name: title,
          },
        });
      } else {
        reportError('Cannot set title when no media is set.');
      }
    },

    get description() {
      const props = getProps();
      return props.videoMedia?.name || props.image?.alt || '';
    },

    set description(value) {
      const description = assert.isNil(value) ? '' : value.toString();
      const props = getProps();

      if (props.videoMedia?.uri) {
        setProps({
          videoMedia: {
            ...props.videoMedia,
            name: description,
          },
        });
      } else if (props.image?.uri) {
        setProps({
          image: {
            ...props.image,
            alt: description,
          },
        });
      } else {
        reportError('Cannot set description when no media is set.');
      }
    },

    get sources() {
      const props = getProps();
      if (props.videoMedia?.uri) {
        if (props.videoMedia.sources) {
          return props.videoMedia.sources?.map(source => ({
            quality: source.quality,
            width: source.width,
            height: source.height,
            url: source.types?.[0]?.uri || '',
            format: source.types?.[0]?.format || 'mp4',
          }));
        }
        if (props.videoMedia.adaptiveSources) {
          return props.videoMedia.adaptiveSources?.map(source => ({
            quality: source.format,
            url: source.uri,
          }));
        }
      }
      return [];
    },

    get fittingType() {
      const props = getProps();
      return props.fittingType || 'fill';
    },

    set fittingType(value: BackgroundFittingType) {
      const validValues: Array<BackgroundFittingType> = [
        'fill',
        'tile',
        'original_size',
      ];
      if (typeof value !== 'string' || !validValues.includes(value)) {
        reportError(
          `The "fittingType" property cannot be set to "${String(value)}". It must be one of: ${validValues.join(', ')}.`,
        );
        return;
      }
      setProps({ fittingType: value });
    },

    get type() {
      return '$w.Background';
    },

    toJSON() {
      const props = getProps();
      return {
        type: '$w.Background',
        src: props.videoMedia?.uri || props.image?.uri || '',
        title: props.videoMedia?.name || props.image?.name || '',
        description: this.description,
        sources: this.sources,
        fittingType: props.fittingType,
      };
    },
  };
}
